import http from "../../../http-common.js";

// Latest-wins guard: overlapping list requests resolve in arbitrary
// order; only the newest may commit (same pattern as payin/actions.js).
let usersListRequestSeq = 0;

export default {
  async getUsers(context, params) {
    const requestSeq = ++usersListRequestSeq;
    try {
      const qs = new URLSearchParams();
      if (params.first) qs.set("first", params.first);
      if (params.page) qs.set("page", params.page);
      // status + search are now server-side: client-side filtering only saw
      // the current page so banks ranked low by ORDER BY (e.g. inactive)
      // looked invisible until activated.
      if (params.status) qs.set("status", params.status);
      if (params.search) qs.set("search", params.search);
      // Optional branch filter — admin/superadmin/vendor can narrow the
      // bank list to a specific branch (subadmin id). Subadmin's own
      // branch is enforced server-side and ignores this param.
      if (params.branchId) qs.set("branchId", params.branchId);
      const qsString = qs.toString();
      const url = qsString ? `/users?${qsString}` : "/users";

      const response = await http.get(url);

      if (response.status == 200) {
        const responseData = response.data.data;
        const pagination = response.data.pagination;
        const users = [];
        // ⚠️ This mapper hand-picks fields from the API response — any
        // `users` column NOT listed below silently drops on refresh.
        // When you add a new column to the users table, also list it
        // here OR your toggle/state will appear correct after the click
        // (the mutation patches it in-place) but snap back to its
        // default after F5. Recently bitten: blacklisted_by_vendor and
        // can_approve_payin_manually.
        for (const key in responseData) {
          const user = {
            username: responseData[key].username,
            role: responseData[key].role,
            status: responseData[key].status,
            is_deleted: responseData[key].is_deleted || 0,
            upiId: responseData[key].upiid,
            name: responseData[key].name,
            email: responseData[key].email,
            phone: responseData[key].phone,
            payIn: responseData[key].payIn,
            payOut: responseData[key].payOut,
            payInLimit: responseData[key].payInLimit,
            payOutLimit: responseData[key].payOutLimit,
            balance: responseData[key].balance,
            payInCommission: responseData[key].payInCommission,
            payOutCommission: responseData[key].payOutCommission,
            isLoggedIn: responseData[key].isLoggedIn,
            approvedCount: responseData[key].approvedCount,
            approvedAmount: responseData[key].approvedAmount,
            pendingCount: responseData[key].pendingCount,
            pendingAmount: responseData[key].pendingAmount,
            website: responseData[key].website,
            is_utr_enabled: responseData[key].is_utr_enabled,
            isPayNow: responseData[key].isPayNow ? true : false,
            paymentMethod: responseData[key].paymentMethod,
            merchantCode: responseData[key].merchantCode,
            merchantName: responseData[key].merchantName,
            uniqueIdentifier: responseData[key].uniqueIdentifier,
            accountHolderName: responseData[key].accountHolderName,
            accountNumber: responseData[key].accountNumber,
            ifsc: responseData[key].ifsc,
            bankName: responseData[key].bankName,
            extensionId: responseData[key].extensionId,
            qr: responseData[key].qr,
            branch_id: responseData[key].branch_id,
            branchUsername: responseData[key].branchUsername,
            branchName: responseData[key].branchName,
            minOrderAmount: responseData[key].minOrderAmount,
            maxOrderAmount: responseData[key].maxOrderAmount,
            maxOrders: responseData[key].maxOrders,
            maxBalance: responseData[key].maxBalance,
            // Vendor blacklist flag (03062026). Without copying this
            // here, UserItem's isBlacklisted computed sees `undefined`
            // on every refresh — the toggle ends up showing "Not
            // Blacklisted" even though the DB has the flag set.
            blacklisted_by_vendor: responseData[key].blacklisted_by_vendor,
            // Per-branch manual-approve permission (added on subadmin
            // users in the manual-approve PR). The bank list doesn't
            // surface subadmin rows today so this is defensive — if
            // this endpoint ever returns subadmin rows the toggle
            // needs to round-trip cleanly here too.
            can_approve_payin_manually: responseData[key].can_approve_payin_manually,
            // Daily payin limit template + last reset stamp (18062026).
            // UserForm.vue reads per_day_payin_limit on edit to populate
            // the "Daily Pay In Limit" field + derive the "Used today"
            // hint as (template − payInLimit). Without this here, the
            // hint silently shows wrong values after a refresh.
            per_day_payin_limit: responseData[key].per_day_payin_limit,
            payin_last_reset_date: responseData[key].payin_last_reset_date,
          };
          users.push(user);
        }
        if (requestSeq !== usersListRequestSeq) return; // stale response — drop
        context.commit("setUsers", users);
        context.commit("setPagination", pagination);
      } else {
        const error = new Error("Something went wrong!");
        throw error;
      }
    } catch (error) {
      const errorMessage = {
        status: error.response.data.success,
        message: new Error(error.response.data.message || "Something failed!"),
      };
      throw errorMessage;
    }
  },
  async createUser(context, data) {
    const qrPreview = data.qrPreview;
    delete data.qrPreview;
    delete data.qr;
    let userData;
    try {
      const response = await http.put("/users", data, {
        headers: {
        'Content-Type': 'multipart/form-data',
        },
      });
      if (response.status == 201) {
        userData = {
          username: data.username,
          password: data.password,
          upiId: data.upiid,
          role: data.role,
          name: data.name,
          email: data.email,
          phone: data.phone,
          payIn: data.payIn,
          payOut: data.payOut,
          payInLimit: data.payInLimit,
          payOutLimit: data.payOutLimit,
          balance: data.balance,
          payInCommission: data.payInCommission,
          payOutCommission: data.payOutCommission,
          website: data.website,
          is_utr_enabled: data.is_utr_enabled,
          isPayNow: data.isPayNow ? true : false,
          status: 0,
          paymentMethod: data.paymentMethod,
          merchantCode: data.merchantCode,
          merchantName: data.merchantName,
          uniqueIdentifier: data.uniqueIdentifier,
          accountNumber: data.accountNumber,
          accountHolderName: data.accountHolderName,
          ifsc: data.ifsc,
          bankName: data.bankName,
          extensionId: data.extensionId,
          qr: qrPreview,
          approvedCount: 0,
          approvedAmount: 0,
          pendingCount: 0,
          pendingAmount: 0,
          branch_id: data.branch_id,
          minOrderAmount: data.minOrderAmount,
          maxOrderAmount: data.maxOrderAmount,
          maxOrders: data.maxOrders,
          maxBalance: data.maxBalance,
          per_day_payin_limit: data.per_day_payin_limit,
        };
      } else {
        const error = new Error("Something went wrong!");
        throw error;
      }
    } catch (error) {
      const errorMessage = new Error(
        error.response.data.message || "Something failed!"
      );
      throw errorMessage;
    }

    context.commit("addUser", userData);
  },
  async updateUser(context, data) {
    const username = data.username;
    const status = data.status;
    const qrPreview = data.qrPreview;
    delete data.username;
    delete data.qrPreview;
    delete data.qr;
    let response;
    try {
      response = await http.post(`/users/${username}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (response.status != 200) {
        throw new Error("Something went wrong!");
      }
    } catch (error) {
      const errorMessage = new Error(
        error.response?.data?.message || "Something failed!"
      );
      throw errorMessage;
    }

    // Build merge payload — only include fields that were actually provided in the form
    const userData = { username };
    const fieldMap = {
      upiid: "upiId",
      name: "name",
      email: "email",
      phone: "phone",
      payIn: "payIn",
      payOut: "payOut",
      payInLimit: "payInLimit",
      payOutLimit: "payOutLimit",
      balance: "balance",
      payInCommission: "payInCommission",
      payOutCommission: "payOutCommission",
      website: "website",
      is_utr_enabled: "is_utr_enabled",
      paymentMethod: "paymentMethod",
      merchantCode: "merchantCode",
      merchantName: "merchantName",
      uniqueIdentifier: "uniqueIdentifier",
      accountNumber: "accountNumber",
      accountHolderName: "accountHolderName",
      ifsc: "ifsc",
      bankName: "bankName",
      extensionId: "extensionId",
      branch_id: "branch_id",
      minOrderAmount: "minOrderAmount",
      maxOrderAmount: "maxOrderAmount",
      maxOrders: "maxOrders",
      maxBalance: "maxBalance",
    };
    for (const [src, dest] of Object.entries(fieldMap)) {
      if (data[src] !== undefined) userData[dest] = data[src];
    }
    if (data.isPayNow !== undefined) userData.isPayNow = !!data.isPayNow;
    if (data.password) userData.password = data.password;
    if (qrPreview !== undefined) userData.qr = qrPreview;
    if (status !== undefined) userData.status = status;

    // Daily payin limit (18062026). When the vendor edits the daily
    // template, the backend applies the X − Y math and rewrites
    // payInLimit + payin_last_reset_date alongside per_day_payin_limit.
    // We can't recompute those values client-side from the form alone
    // (X is in the request, but Y depends on the server's view of the
    // bank row at write time), so we read them from the response's
    // `computed` envelope and patch the local store row. Without this
    // the form would show stale values when reopened until the next
    // page refresh re-runs getUsers.
    const computed = (response && response.data && response.data.computed) || {};
    if (computed.per_day_payin_limit !== undefined) {
      userData.per_day_payin_limit = computed.per_day_payin_limit;
    }
    if (computed.payInLimit !== undefined) {
      userData.payInLimit = computed.payInLimit;
    }
    if (computed.payin_last_reset_date !== undefined) {
      userData.payin_last_reset_date = computed.payin_last_reset_date;
    }

    context.commit("updateUser", userData);
  },
  async deleteUser(context, user) {
    const username = user.username;

    try {
      const response = await http.delete(`/users/${username}`);
      if (response.status == 200) {
        context.commit("deleteUser", username);
      } else {
        const error = new Error("Something went wrong!");
        throw error;
      }
    } catch (error) {
      const errorMessage = new Error(
        error.response.data.message || "Something failed!"
      );
      throw errorMessage;
    }
  },

  async changeUserStatus(context, user) {
    const username = user.username;
    const status = user.status;

    const data = {
      status: status,
    };

    try {
      const response = await http.post(`/users/${username}/status`, data);
      if (response.status == 200) {
        const payload = {
          username: username,
          status: status,
        };
        context.commit("changeUserStatus", payload);
      } else {
        const error = new Error("Something went wrong!");
        throw error;
      }
    } catch (error) {
      const errorMessage = new Error(
        error.response.data.message || "Something failed!"
      );
      throw errorMessage;
    }
  },
  // Vendor blacklist toggle. The backend atomically flips
  // `blacklisted_by_vendor` AND (on blacklist only) `status=0`. The
  // response carries the post-update values for both columns so we
  // mirror them locally without a refetch.
  async changeUserBlacklist(context, user) {
    const username = user.username;
    const desired = user.blacklisted_by_vendor ? 1 : 0;
    try {
      const response = await http.post(`/users/${username}/blacklist`, {
        blacklisted_by_vendor: desired,
      });
      if (response.status === 200) {
        const data = response.data || {};
        context.commit("changeUserBlacklist", {
          username,
          blacklisted_by_vendor:
            data.blacklisted_by_vendor !== undefined
              ? data.blacklisted_by_vendor
              : desired,
          // status only comes back on blacklist=1 (auto-flipped to 0).
          // On unblacklist the backend doesn't change status, but it
          // does echo the existing value as 0 — apply consistently.
          status: data.status,
        });
      } else {
        throw new Error("Something went wrong!");
      }
    } catch (error) {
      const errorMessage = new Error(
        (error.response && error.response.data && error.response.data.message) ||
          error.message ||
          "Something failed!"
      );
      throw errorMessage;
    }
  },
  async changeUserLoginStatus(context, user) {
    const username = user.username;
    const isLoggedIn = user.isLoggedIn;
    try {
      const response = await http.put(`/users/${username}/${isLoggedIn}`);
      if (response.status == 200) {
        const payload = {
          username: username,
          isLoggedIn: isLoggedIn,
        };
        context.commit("changeUserLoginStatus", payload);
      } else {
        const error = new Error("Something went wrong!");
        throw error;
      }
    } catch (error) {
      const errorMessage = new Error(
        error.response.data.message || "Something failed!"
      );
      throw errorMessage;
    }
  },
};
